import type { SolfegeHandSignGlyphMetrics } from 'design/src/fonts/utheory-solfege';
import {
  doSelf,
  faSelf,
  fiSelf,
  laSelf,
  leSelf,
  meSelf,
  miSelf,
  reSelf,
  siSelf,
  solfegeHandSignGlyphToHtmlString,
  solSelf,
  teSelf,
  tiSelf,
} from 'design/src/fonts/utheory-solfege';
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import { isModeName } from 'shared/definitions/musicTheory/scales';
import type {
  SolfegeSystemName,
  ScaleDegree,
} from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import {
  solfegeSystems,
  scaleDegreeNamesMajor,
  scaleDegreeNamesMinor,
} from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import { scaleDegreesArrayByMode } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';

export const handSignsDictionary: Record<string, SolfegeHandSignGlyphMetrics & { alt: string }> = {
  Do: { ...doSelf, alt: 'A closed fist held level' },
  Re: { ...reSelf, alt: 'A flat hand, palm to the floor, inclined slightly up from the wrist' },
  Me: {
    ...meSelf,
    alt: 'A flat hand, palm to the floor, angling down to the floor from the wrist to fingers',
  },
  Mi: { ...miSelf, alt: 'A flat hand, palm to the floor, held parallel to the floor' },
  Fa: { ...faSelf, alt: 'A hand in a thumbs-down shape' },
  Fi: { ...fiSelf, alt: 'A hand in a thumbs-up shape' },
  Sol: { ...solSelf, alt: 'A flat hand with the palm facing the viewer' },
  Si: { ...siSelf, alt: 'A flat hand, fingers spread wide, with the palm facing the viewer' },
  Le: {
    ...leSelf,
    alt: 'A hand making a C-shape, with the opening of the C angled down to the floor',
  },
  La: { ...laSelf, alt: 'A hand making a C-shape, opening to the right and slightly down' },
  Te: { ...teSelf, alt: 'A hand with the index finger pointing downwards' },
  Ti: { ...tiSelf, alt: 'A hand with the index finger pointing upwards' },
};

/**
 * Get arrays of solfege/scale degrees/hand signs for the indicated scale.
 * @param mode Major, naturalMinor, harmonicMinor, melodicMinorAscending, melodicMinorDescending
 */
export function getSolfegeSystemsMapForScaleMode(
  mode: ModeName | 'allMinors',
  solfegeSystem: SolfegeSystemName
): {
  /** The degrees by name (1) and a formatted html displayable degree string */
  scaleDegrees: { name: ScaleDegree; displayString: string }[];
  /** The long version of the names, e.g. "tonic", "raised submediant", etc...  */
  scaleDegreeNames: string[];
  /** "Do", "Re", etc... */
  solfege: string[];
  /** Glyphs and metrics for the hand signs */
  handSigns: (SolfegeHandSignGlyphMetrics & { alt: string; svgString: string })[];
} {
  if (!isModeName(mode) && mode !== 'allMinors') {
    throw Error(`${mode} is not a valid mode name.`);
  }

  // We have to be able to return hand signs, so, here's what we do if our system is scale degrees.
  if (solfegeSystem === 'scaleDegrees') {
    solfegeSystem = 'solfegeDoMinor';
  }

  const degreeKeys = scaleDegreesArrayByMode[mode];
  const degreesTable = solfegeSystems[mode === 'Major' ? 'Major' : 'minor'].scaleDegrees;
  const solfegeTable = solfegeSystems[mode === 'Major' ? 'Major' : 'minor'][solfegeSystem];
  const scaleDegrees = degreeKeys.map((d) => ({
    displayString: `${degreesTable[d]}`,
    name: d,
  }));
  const solfege: string[] = degreeKeys.map((d) => solfegeTable[d]);
  const handSigns = solfege.map((s) => {
    const glyph = handSignsDictionary[s];
    const svgString = solfegeHandSignGlyphToHtmlString(glyph, glyph.alt, { stroke: 20 });
    return {
      ...glyph,
      svgString,
    };
  });

  const degreeNamesTable = mode === 'Major' ? scaleDegreeNamesMajor : scaleDegreeNamesMinor;
  const scaleDegreeNames: string[] = degreeKeys.map((sd) => degreeNamesTable[sd]);

  return {
    scaleDegrees,
    scaleDegreeNames,
    solfege,
    handSigns,
  };
}
